import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {environment} from '@environments/environment';
import {catchError, Observable, throwError} from 'rxjs';
import {
  ApiResponse, 
  Condominium, 
  CondominiumFull, 
  CondominiumDetail, 
  CondominiumCreate,
  CondominiumDashboard,
  CondominiumUpdate,
  CondominiumLogo,
  CondominiumLogoUpdate,
  MaintenanceFeeUpdate,
  Unit
} from '@core/types';

@Injectable({
  providedIn: 'root',
})
export class CondominiumService {
  baseUrl: string;

  constructor(private httpClient: HttpClient) {
    this.baseUrl = environment.api + '/condominium';
  }

  create(model: CondominiumCreate): Observable<ApiResponse<string>> {
    return this.httpClient.post<ApiResponse<string>>(
      this.baseUrl,
      model
    ).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message);
      })
    );
  }

  update(model: CondominiumFull): Observable<ApiResponse<void>> {
    return this.httpClient.put<ApiResponse<void>>(
      this.baseUrl,
      model
    ).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message);
      })
    );
  }

  get(id: string): Observable<ApiResponse<CondominiumFull>> {
    return this.httpClient.get<ApiResponse<CondominiumFull>>(
      `${this.baseUrl}/${id}`
    ).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message);
      })
    );
  }

  getDetail(id: string): Observable<ApiResponse<CondominiumDetail>> {
    return this.httpClient.get<ApiResponse<CondominiumDetail>>(
      `${this.baseUrl}/${id}/detail`
    ).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message );
      })
    );
  }

  updateUnit(unit: Unit): Observable<ApiResponse<void>> {
    return this.httpClient.put<ApiResponse<void>>(
      `${this.baseUrl}/unit`,
      unit
    ).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message );
      })
    );
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.httpClient.delete<ApiResponse<void>>(
      `${this.baseUrl}/${id}`
    ).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message);
      })
    );
  }

  switch(): Observable<ApiResponse<Condominium[]>> {
    return this.httpClient.get<ApiResponse<Condominium[]>>(`${this.baseUrl}/switch`)
      .pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          return throwError(() => errorResponse.error?.message );
        })
      );
  }

  getDashboard(): Observable<ApiResponse<CondominiumDashboard>> {
    return this.httpClient.get<ApiResponse<CondominiumDashboard>>(
      `${this.baseUrl}/dashboard`
    ).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message);
      })
    );
  }

  updateCondominium(model: CondominiumUpdate): Observable<ApiResponse<void>> {
    return this.httpClient.put<ApiResponse<void>>(
      this.baseUrl,
      model
    ).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message);
      })
    );
  }

  updateMaintenanceFeeBulk(model: MaintenanceFeeUpdate): Observable<ApiResponse<void>> {
    return this.httpClient.put<ApiResponse<void>>(
      `${this.baseUrl}/maintenance/bulk`,
      model
    ).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message);
      })
    );
  }

  getLogo(): Observable<ApiResponse<CondominiumLogo | null>> {
    return this.httpClient.get<ApiResponse<CondominiumLogo | null>>(
      `${this.baseUrl}/logo`
    ).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message);
      })
    );
  }

  updateLogo(model: CondominiumLogoUpdate): Observable<ApiResponse<void>> {
    return this.httpClient.put<ApiResponse<void>>(
      `${this.baseUrl}/logo`,
      model
    ).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message);
      })
    );
  }

  deleteLogo(): Observable<ApiResponse<void>> {
    return this.httpClient.delete<ApiResponse<void>>(
      `${this.baseUrl}/logo`
    ).pipe(
      catchError((errorResponse: HttpErrorResponse) => {
        return throwError(() => errorResponse.error?.message);
      })
    );
  }
}
